import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useQuery as useConvexQuery, useMutation as useConvexMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { ArrowLeft, Ban, CheckCircle, Plus, X, Bell, Send, Edit3, AlertTriangle, Activity, Clock, Smartphone, Trash2, BarChart3, Globe } from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';
import Textarea from '../../components/Textarea';
import Input from '../../components/Input';
import Select from '../../components/Select';
import LoadingSpinner from '../../components/LoadingSpinner';
import Switch from '../../components/Switch';
import { useAdmin } from '../../contexts/AdminContext';
import {
    getUser,
    banUser,
    unbanUser,
    addUserAccountType,
    removeUserAccountType,
    sendPushToUser,
    changeUsername,
    hardDeleteUser,
    toggleAnalyticsExclusion,
    addUserToEnvironment,
    removeUserFromEnvironment
} from '../../utils/api';
import { getUserActivity } from '../../utils/analyticsApi';
import {
    formatDate,
    formatDateTime,
    formatTimeAgo,
    getAccountTypeBadgeVariant,
    getAccountTypeLabel,
} from '../../utils/helpers';
import toast from 'react-hot-toast';

// User Activity Card Component - shows analytics data for the user
function UserActivityCard({ clerkId }) {
    const { data: activityData, isLoading } = useQuery({
        queryKey: ['user-activity', clerkId],
        queryFn: () => getUserActivity(clerkId, { limit: 20 }),
        enabled: !!clerkId,
        staleTime: 5 * 60 * 1000 // 5 minutes
    });

    const activity = activityData?.data;

    if (!clerkId) {
        return null;
    }

    const formatEventTime = (timestamp) => {
        const date = new Date(timestamp);
        return date.toLocaleString();
    };

    const getEventIcon = (eventType) => {
        switch (eventType) {
            case 'screen_view':
                return <Smartphone size={14} className="text-blue" />;
            case 'feature_used':
                return <Activity size={14} className="text-green" />;
            case 'session_start':
            case 'session_end':
                return <Clock size={14} className="text-purple" />;
            default:
                return <Activity size={14} className="text-muted-light dark:text-muted-dark" />;
        }
    };

    const getEventLabel = (event) => {
        switch (event.eventType) {
            case 'screen_view':
                return `Viewed ${event.properties?.screenName || event.eventName}`;
            case 'feature_used':
                return `Used ${event.properties?.featureName || event.eventName}`;
            case 'session_start':
                return 'Started session';
            case 'session_end':
                return `Ended session (${formatDuration(event.properties?.duration)})`;
            case 'error':
                return `Error: ${event.properties?.errorMessage?.substring(0, 50) || 'Unknown'}`;
            default:
                return event.eventName;
        }
    };

    const formatDuration = (ms) => {
        if (!ms) return '0s';
        const seconds = Math.floor(ms / 1000);
        const minutes = Math.floor(seconds / 60);
        if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
        return `${seconds}s`;
    };

    return (
        <Card title="Recent Activity (Analytics)" icon={<Activity size={20} />}>
            {isLoading ? (
                <div className="flex items-center justify-center py-8">
                    <LoadingSpinner />
                </div>
            ) : !activity || activity.events?.length === 0 ? (
                <p className="text-muted-light dark:text-muted-dark text-center py-4">
                    No analytics data available for this user
                </p>
            ) : (
                <div className="space-y-4">
                    {/* First seen info */}
                    {activity.firstSeen && (
                        <div className="p-3 bg-accent-light dark:bg-accent-dark rounded-lg">
                            <p className="text-sm text-muted-light dark:text-muted-dark">First seen</p>
                            <p className="font-medium">{activity.firstSeen.date}</p>
                            <div className="flex items-center gap-2 mt-1 text-xs text-muted-light dark:text-muted-dark">
                                <span className="capitalize">{activity.firstSeen.platform}</span>
                                <span>•</span>
                                <span>v{activity.firstSeen.appVersion}</span>
                            </div>
                        </div>
                    )}

                    {/* Session stats */}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 bg-accent-light dark:bg-accent-dark rounded-lg">
                            <p className="text-sm text-muted-light dark:text-muted-dark">Total Sessions</p>
                            <p className="text-xl font-bold">{activity.totalSessions || 0}</p>
                        </div>
                        <div className="p-3 bg-accent-light dark:bg-accent-dark rounded-lg">
                            <p className="text-sm text-muted-light dark:text-muted-dark">Recent Events</p>
                            <p className="text-xl font-bold">{activity.totalEvents || 0}</p>
                        </div>
                    </div>

                    {/* Recent events */}
                    <div>
                        <p className="text-sm font-medium mb-2">Recent Events</p>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                            {activity.events?.slice(0, 15).map((event) => (
                                <div
                                    key={event._id}
                                    className="flex items-center gap-3 p-2 bg-accent-light dark:bg-accent-dark rounded text-sm"
                                >
                                    {getEventIcon(event.eventType)}
                                    <div className="flex-1 min-w-0">
                                        <p className="truncate">{getEventLabel(event)}</p>
                                    </div>
                                    <span className="text-xs text-muted-light dark:text-muted-dark whitespace-nowrap">
                                        {formatEventTime(event.timestamp)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </Card>
    );
}

export default function UserDetail() {
    const { userId } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { admin } = useAdmin();

    // Modal states
    const [showBanModal, setShowBanModal] = useState(false);
    const [showUnbanModal, setShowUnbanModal] = useState(false);
    const [showAddTypeModal, setShowAddTypeModal] = useState(false);
    const [showPushModal, setShowPushModal] = useState(false);
    const [showRemoveTypeModal, setShowRemoveTypeModal] = useState(false);
    const [showChangeUsernameModal, setShowChangeUsernameModal] = useState(false);
    const [showHardDeleteModal, setShowHardDeleteModal] = useState(false);

    // Form states
    const [banReason, setBanReason] = useState('');
    const [selectedAccountType, setSelectedAccountType] = useState('');
    const [pushNotification, setPushNotification] = useState({ title: '', body: '', deepLink: '' });

    // Remove account type confirmation states
    const [typeToRemove, setTypeToRemove] = useState(null);
    const [removeTypeConfirmation, setRemoveTypeConfirmation] = useState('');

    // Change username states
    const [newUsername, setNewUsername] = useState('');
    const [usernameReason, setUsernameReason] = useState('');
    const [usernameConfirmation, setUsernameConfirmation] = useState('');

    // Hard delete confirmation states
    const [hardDeleteConfirmUsername, setHardDeleteConfirmUsername] = useState('');
    const [hardDeleteConfirmDelete, setHardDeleteConfirmDelete] = useState('');
    const [hardDeleteAcknowledged, setHardDeleteAcknowledged] = useState(false);

    // Environment management states
    const [showAddEnvModal, setShowAddEnvModal] = useState(false);
    const [selectedEnvironment, setSelectedEnvironment] = useState('');

    // Convex mutations for activity logging
    const logAction = useConvexMutation(api.activityLogs.logAction);

    // Fetch sent push notifications for this user
    const sentNotifications = useConvexQuery(
        api.activityLogs.getLogsByTarget,
        { targetType: 'user', targetId: userId, limit: 10 }
    );

    // Filter only push notifications
    const sentPushNotifications = sentNotifications?.filter(
        (log) => log.action === 'push_notification_sent'
    ) || [];

    // Fetch user data
    const { data: userData, isLoading } = useQuery({
        queryKey: ['user', userId],
        queryFn: () => getUser(userId),
    });

    const user = userData?.data;

    // Ban user mutation
    const banMutation = useMutation({
        mutationFn: () => banUser(userId, banReason),
        onSuccess: () => {
            toast.success('User banned successfully');
            queryClient.invalidateQueries(['user', userId]);
            setShowBanModal(false);
            setBanReason('');
        },
        onError: (error) => {
            toast.error(error.message);
        },
    });

    // Unban user mutation
    const unbanMutation = useMutation({
        mutationFn: () => unbanUser(userId),
        onSuccess: () => {
            toast.success('User unbanned successfully');
            queryClient.invalidateQueries(['user', userId]);
            setShowUnbanModal(false);
        },
        onError: (error) => {
            toast.error(error.message);
        },
    });

    // Add account type mutation
    const addTypeMutation = useMutation({
        mutationFn: () => addUserAccountType(userId, selectedAccountType),
        onSuccess: () => {
            toast.success('Account type added successfully');
            queryClient.invalidateQueries(['user', userId]);
            setShowAddTypeModal(false);
            setSelectedAccountType('');
        },
        onError: (error) => {
            toast.error(error.message);
        },
    });

    // Remove account type mutation
    const removeTypeMutation = useMutation({
        mutationFn: (accountType) => removeUserAccountType(userId, accountType),
        onSuccess: async () => {
            toast.success(`${getAccountTypeLabel(typeToRemove)} removed successfully`);
            queryClient.invalidateQueries(['user', userId]);

            // Log the action
            if (admin?._id) {
                try {
                    await logAction({
                        adminId: admin._id,
                        action: 'account_type_removed',
                        targetType: 'user',
                        targetId: userId,
                        details: {
                            metadata: {
                                accountType: typeToRemove,
                                username: user?.username,
                            }
                        }
                    });
                } catch (err) {
                    console.error('Failed to log account type removal:', err);
                }
            }

            closeRemoveTypeModal();
        },
        onError: (error) => {
            toast.error(error.message);
        },
    });

    // Change username mutation
    const changeUsernameMutation = useMutation({
        mutationFn: () => changeUsername(userId, newUsername, usernameReason),
        onSuccess: async (data) => {
            toast.success(data?.message || 'Username changed successfully');
            queryClient.invalidateQueries(['user', userId]);

            // Log the action
            if (admin?._id) {
                try {
                    await logAction({
                        adminId: admin._id,
                        action: 'username_changed',
                        targetType: 'user',
                        targetId: userId,
                        details: {
                            metadata: {
                                oldUsername: user?.username,
                                newUsername: newUsername.toLowerCase().trim(),
                                reason: usernameReason,
                            }
                        }
                    });
                } catch (err) {
                    console.error('Failed to log username change:', err);
                }
            }

            closeChangeUsernameModal();
        },
        onError: (error) => {
            toast.error(error.message);
        },
    });

    // Send push notification mutation
    const pushMutation = useMutation({
        mutationFn: () => sendPushToUser(userId, {
            title: pushNotification.title,
            body: pushNotification.body,
            deepLink: pushNotification.deepLink || undefined,
        }),
        onSuccess: async (data) => {
            toast.success(data?.message || 'Push notification sent');

            // Log the action to admin activity logs
            if (admin?._id) {
                try {
                    await logAction({
                        adminId: admin._id,
                        action: 'push_notification_sent',
                        targetType: 'user',
                        targetId: userId,
                        details: {
                            metadata: {
                                title: pushNotification.title,
                                body: pushNotification.body,
                                deepLink: pushNotification.deepLink || null,
                                username: user?.username,
                            }
                        }
                    });
                } catch (err) {
                    console.error('Failed to log push notification:', err);
                }
            }

            setShowPushModal(false);
            setPushNotification({ title: '', body: '', deepLink: '' });
        },
        onError: (error) => {
            toast.error(error.message);
        },
    });

    // Hard delete mutation (admin dev cleanup)
    const hardDeleteMutation = useMutation({
        mutationFn: () => hardDeleteUser(userId),
        onSuccess: async (data) => {
            toast.success(data?.message || 'User completely removed from the system');

            // Log the action
            if (admin?._id) {
                try {
                    await logAction({
                        adminId: admin._id,
                        action: 'user_hard_deleted',
                        targetType: 'user',
                        targetId: userId,
                        details: {
                            metadata: {
                                username: user?.username,
                                email: user?.email,
                            }
                        }
                    });
                } catch (err) {
                    console.error('Failed to log hard delete:', err);
                }
            }

            navigate('/users');
        },
        onError: (error) => {
            toast.error(error.message);
        },
    });

    // Analytics exclusion mutation
    const analyticsExclusionMutation = useMutation({
        mutationFn: (exclude) => toggleAnalyticsExclusion(userId, exclude),
        onSuccess: async (data) => {
            const excluded = data?.data?.excludeFromAnalytics;
            toast.success(data?.message || `User ${excluded ? 'excluded from' : 'included in'} analytics`);
            queryClient.invalidateQueries(['user', userId]);

            // Log the action
            if (admin?._id) {
                try {
                    await logAction({
                        adminId: admin._id,
                        action: excluded ? 'analytics_exclusion_enabled' : 'analytics_exclusion_disabled',
                        targetType: 'user',
                        targetId: userId,
                        details: {
                            metadata: {
                                username: user?.username,
                                excludeFromAnalytics: excluded,
                            }
                        }
                    });
                } catch (err) {
                    console.error('Failed to log analytics exclusion toggle:', err);
                }
            }
        },
        onError: (error) => {
            toast.error(error.message);
        },
    });

    // Add user to environment mutation
    const addEnvMutation = useMutation({
        mutationFn: () => addUserToEnvironment(userId, selectedEnvironment),
        onSuccess: async (data) => {
            toast.success(data?.message || `User added to ${selectedEnvironment} environment`);
            queryClient.invalidateQueries(['user', userId]);

            if (admin?._id) {
                try {
                    await logAction({
                        adminId: admin._id,
                        action: 'user_environment_added',
                        targetType: 'user',
                        targetId: userId,
                        details: {
                            metadata: {
                                username: user?.username,
                                environment: selectedEnvironment,
                            }
                        }
                    });
                } catch (err) {
                    console.error('Failed to log environment add:', err);
                }
            }

            setShowAddEnvModal(false);
            setSelectedEnvironment('');
        },
        onError: (error) => {
            toast.error(error.message);
        },
    });

    // Remove user from environment mutation
    const removeEnvMutation = useMutation({
        mutationFn: (environment) => removeUserFromEnvironment(userId, environment),
        onSuccess: async (data, environment) => {
            toast.success(data?.message || `User removed from ${environment} environment`);
            queryClient.invalidateQueries(['user', userId]);

            if (admin?._id) {
                try {
                    await logAction({
                        adminId: admin._id,
                        action: 'user_environment_removed',
                        targetType: 'user',
                        targetId: userId,
                        details: {
                            metadata: {
                                username: user?.username,
                                environment: environment,
                            }
                        }
                    });
                } catch (err) {
                    console.error('Failed to log environment removal:', err);
                }
            }
        },
        onError: (error) => {
            toast.error(error.message);
        },
    });

    // Helper functions for modals
    const openRemoveTypeModal = (type) => {
        setTypeToRemove(type);
        setRemoveTypeConfirmation('');
        setShowRemoveTypeModal(true);
    };

    const closeRemoveTypeModal = () => {
        setShowRemoveTypeModal(false);
        setTypeToRemove(null);
        setRemoveTypeConfirmation('');
    };

    const openChangeUsernameModal = () => {
        setNewUsername('');
        setUsernameReason('');
        setUsernameConfirmation('');
        setShowChangeUsernameModal(true);
    };

    const closeChangeUsernameModal = () => {
        setShowChangeUsernameModal(false);
        setNewUsername('');
        setUsernameReason('');
        setUsernameConfirmation('');
    };

    const openHardDeleteModal = () => {
        setHardDeleteConfirmUsername('');
        setHardDeleteConfirmDelete('');
        setHardDeleteAcknowledged(false);
        setShowHardDeleteModal(true);
    };

    const closeHardDeleteModal = () => {
        setShowHardDeleteModal(false);
        setHardDeleteConfirmUsername('');
        setHardDeleteConfirmDelete('');
        setHardDeleteAcknowledged(false);
    };

    // Validation helpers
    const isRemoveTypeConfirmed = removeTypeConfirmation === user?.username;
    const isUsernameChangeConfirmed = usernameConfirmation === user?.username && newUsername.trim().length >= 3;
    const isHardDeleteConfirmed = hardDeleteAcknowledged &&
        hardDeleteConfirmUsername === user?.username &&
        hardDeleteConfirmDelete === 'DELETE';

    // Check if current admin has super_admin privilege
    const isSuperAdmin = admin?.privileges?.includes('super_admin');

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <LoadingSpinner size="large" />
            </div>
        );
    }

    if (!user) {
        return (
            <div className="text-center py-12">
                <h2 className="text-2xl font-bold mb-2">User not found</h2>
                <Button onClick={() => navigate('/users')}>Back to Users</Button>
            </div>
        );
    }

    const accountTypes = user.accountTypes || [];
    const availableTypes = ['verified', 'reporter', 'community', 'special', 'super'].filter(
        (type) => !accountTypes.includes(type)
    );

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="secondary" onClick={() => navigate('/users')}>
                        <ArrowLeft size={20} />
                    </Button>
                    <div>
                        <h1 className="text-3xl font-bold">User Details</h1>
                        <p className="text-muted-light dark:text-muted-dark">
                            Manage user account and permissions
                        </p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button variant="secondary" onClick={() => setShowPushModal(true)}>
                        <Bell size={20} />
                        Send Push
                    </Button>
                    {user.isBanned ? (
                        <Button variant="success" onClick={() => setShowUnbanModal(true)}>
                            <CheckCircle size={20} />
                            Unban User
                        </Button>
                    ) : (
                        <Button variant="danger" onClick={() => setShowBanModal(true)}>
                            <Ban size={20} />
                            Ban User
                        </Button>
                    )}
                    {isSuperAdmin && (
                        <Button
                            variant="danger"
                            onClick={openHardDeleteModal}
                            className="!bg-red-600 hover:!bg-red-700"
                            title="Permanently delete user and all their data"
                        >
                            <Trash2 size={20} />
                            Hard Delete
                        </Button>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Profile Card */}
                <Card className="lg:col-span-1">
                    <div className="text-center">
                        {user.profilePicture ? (
                            <img
                                src={user.profilePicture}
                                alt={user.username}
                                className="w-32 h-32 rounded-full object-cover mx-auto mb-4"
                            />
                        ) : (
                            <div className="w-32 h-32 rounded-full bg-accent-light dark:bg-accent-dark flex items-center justify-center mx-auto mb-4">
                                <span className="text-4xl font-bold">
                                    {user.username?.charAt(0)?.toUpperCase()}
                                </span>
                            </div>
                        )}
                        <div className="flex items-center justify-center gap-2 mb-1">
                            <h2 className="text-2xl font-bold">@{user.username}</h2>
                            <button
                                onClick={openChangeUsernameModal}
                                className="p-1 rounded hover:bg-accent-light dark:hover:bg-accent-dark transition-colors"
                                title="Change username"
                            >
                                <Edit3 size={16} className="text-muted-light dark:text-muted-dark" />
                            </button>
                        </div>
                        <p className="text-muted-light dark:text-muted-dark mb-4">
                            {user.displayName}
                        </p>
                        {user.isBanned && (
                            <Badge variant="danger" className="mb-4">
                                Banned
                            </Badge>
                        )}
                        {user.bio && (
                            <p className="text-sm text-muted-light dark:text-muted-dark">
                                {user.bio}
                            </p>
                        )}
                    </div>
                </Card>

                {/* Details */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Account Information */}
                    <Card title="Account Information">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-sm text-muted-light dark:text-muted-dark mb-1">
                                    Email
                                </p>
                                <p className="font-medium">{user.email || 'N/A'}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-light dark:text-muted-dark mb-1">
                                    Joined
                                </p>
                                <p className="font-medium">{formatDate(user.createdAt)}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-light dark:text-muted-dark mb-1">
                                    {accountTypes.includes('community') ? 'Members' : 'Followers'}
                                </p>
                                <p className="font-medium">{user.stats?.followers || 0}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-light dark:text-muted-dark mb-1">
                                    Following
                                </p>
                                <p className="font-medium">{user.stats?.following || 0}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-light dark:text-muted-dark mb-1">
                                    Posts
                                </p>
                                <p className="font-medium">{user.stats?.posts || 0}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-light dark:text-muted-dark mb-1">
                                    Last Active
                                </p>
                                <p className="font-medium">{formatDateTime(user.lastSeenAt)}</p>
                            </div>
                        </div>

                        {/* Analytics Exclusion Toggle */}
                        <div className="mt-4 pt-4 border-t border-border-light dark:border-border-dark">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <BarChart3 size={18} className="text-muted-light dark:text-muted-dark" />
                                    <div>
                                        <p className="font-medium text-sm">Exclude from Analytics</p>
                                        <p className="text-xs text-muted-light dark:text-muted-dark">
                                            Hide this user from all analytics calculations
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {user.excludeFromAnalytics && (
                                        <Badge variant="warning" className="text-xs">
                                            Excluded
                                        </Badge>
                                    )}
                                    <Switch
                                        checked={user.excludeFromAnalytics || false}
                                        onChange={(checked) => analyticsExclusionMutation.mutate(checked)}
                                        disabled={analyticsExclusionMutation.isPending}
                                    />
                                </div>
                            </div>
                        </div>
                    </Card>

                    {/* Account Types */}
                    <Card title="Account Types">
                        <div className="flex flex-wrap gap-2 mb-4">
                            {accountTypes.length > 0 ? (
                                accountTypes.map((type) => (
                                    <div
                                        key={type}
                                        className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-light dark:bg-accent-dark"
                                    >
                                        <Badge variant={getAccountTypeBadgeVariant(type)}>
                                            {getAccountTypeLabel(type)}
                                        </Badge>
                                        <button
                                            onClick={() => openRemoveTypeModal(type)}
                                            disabled={removeTypeMutation.isPending}
                                            className="text-red hover:text-red/80"
                                            title={`Remove ${getAccountTypeLabel(type)}`}
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                ))
                            ) : (
                                <p className="text-muted-light dark:text-muted-dark">
                                    Regular user (no special account types)
                                </p>
                            )}
                        </div>
                        {availableTypes.length > 0 && (
                            <Button
                                variant="secondary"
                                onClick={() => setShowAddTypeModal(true)}
                            >
                                <Plus size={20} />
                                Add Account Type
                            </Button>
                        )}
                    </Card>

                    {/* Environment Access */}
                    <Card title="Environment Access" icon={<Globe size={20} />}>
                        <p className="text-sm text-muted-light dark:text-muted-dark mb-4">
                            Control which environments this user can access. Users can only see content from their assigned environments.
                        </p>
                        <div className="flex flex-wrap gap-2 mb-4">
                            {(user.environments || ['prod']).map((env) => (
                                <div
                                    key={env}
                                    className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-light dark:bg-accent-dark"
                                >
                                    <Badge variant={env === 'prod' ? 'success' : env === 'test' ? 'warning' : 'info'}>
                                        {env.toUpperCase()}
                                    </Badge>
                                    {env !== 'prod' && (
                                        <button
                                            onClick={() => removeEnvMutation.mutate(env)}
                                            disabled={removeEnvMutation.isPending}
                                            className="text-red hover:text-red/80"
                                            title={`Remove from ${env}`}
                                        >
                                            <X size={16} />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                        {['dev', 'test'].filter(env => !(user.environments || ['prod']).includes(env)).length > 0 && (
                            <Button
                                variant="secondary"
                                onClick={() => setShowAddEnvModal(true)}
                            >
                                <Plus size={20} />
                                Add Environment
                            </Button>
                        )}
                    </Card>

                    {/* Ban Information */}
                    {user.isBanned && (
                        <Card title="Ban Information">
                            <div className="space-y-2">
                                <div>
                                    <p className="text-sm text-muted-light dark:text-muted-dark">
                                        Reason
                                    </p>
                                    <p className="font-medium">{user.banReason || 'No reason provided'}</p>
                                </div>
                            </div>
                        </Card>
                    )}

                    {/* Sent Push Notifications */}
                    <Card title="Sent Push Notifications" icon={<Send size={20} />}>
                        {sentPushNotifications.length === 0 ? (
                            <p className="text-muted-light dark:text-muted-dark text-center py-4">
                                No push notifications sent to this user yet
                            </p>
                        ) : (
                            <div className="space-y-3">
                                {sentPushNotifications.map((log) => (
                                    <div
                                        key={log._id}
                                        className="p-3 bg-accent-light dark:bg-accent-dark rounded-lg"
                                    >
                                        <div className="flex justify-between items-start mb-2">
                                            <p className="font-medium">
                                                {log.details?.metadata?.title || 'Notification'}
                                            </p>
                                            <span className="text-xs text-muted-light dark:text-muted-dark">
                                                {formatTimeAgo(log.timestamp)}
                                            </span>
                                        </div>
                                        <p className="text-sm text-muted-light dark:text-muted-dark">
                                            {log.details?.metadata?.body || ''}
                                        </p>
                                        {log.details?.metadata?.deepLink && (
                                            <p className="text-xs text-blue mt-1">
                                                Deep link: {log.details.metadata.deepLink}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card>

                    {/* User Activity (Analytics) */}
                    <UserActivityCard clerkId={user?.clerkId} />
                </div>
            </div>

            {/* Ban Modal */}
            <Modal
                isOpen={showBanModal}
                onClose={() => setShowBanModal(false)}
                title="Ban User"
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setShowBanModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={() => banMutation.mutate()}
                            loading={banMutation.isPending}
                            disabled={!banReason.trim()}
                        >
                            Ban User
                        </Button>
                    </>
                }
            >
                <Textarea
                    label="Ban Reason"
                    value={banReason}
                    onChange={(e) => setBanReason(e.target.value)}
                    placeholder="Enter the reason for banning this user..."
                    rows={4}
                    required
                />
            </Modal>

            {/* Unban Modal */}
            <Modal
                isOpen={showUnbanModal}
                onClose={() => setShowUnbanModal(false)}
                title="Unban User"
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setShowUnbanModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="success"
                            onClick={() => unbanMutation.mutate()}
                            loading={unbanMutation.isPending}
                        >
                            Unban User
                        </Button>
                    </>
                }
            >
                <p>Are you sure you want to unban this user?</p>
            </Modal>

            {/* Add Account Type Modal */}
            <Modal
                isOpen={showAddTypeModal}
                onClose={() => setShowAddTypeModal(false)}
                title="Add Account Type"
                footer={
                    <>
                        <Button
                            variant="secondary"
                            onClick={() => setShowAddTypeModal(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => addTypeMutation.mutate()}
                            loading={addTypeMutation.isPending}
                            disabled={!selectedAccountType}
                        >
                            Add Type
                        </Button>
                    </>
                }
            >
                <Select
                    label="Account Type"
                    value={selectedAccountType}
                    onChange={(e) => setSelectedAccountType(e.target.value)}
                    required
                >
                    <option value="">Select account type...</option>
                    {availableTypes.map((type) => (
                        <option key={type} value={type}>
                            {getAccountTypeLabel(type)}
                        </option>
                    ))}
                </Select>
            </Modal>

            {/* Remove Account Type Confirmation Modal */}
            <Modal
                isOpen={showRemoveTypeModal}
                onClose={closeRemoveTypeModal}
                title="Remove Account Type"
                footer={
                    <>
                        <Button variant="secondary" onClick={closeRemoveTypeModal}>
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={() => removeTypeMutation.mutate(typeToRemove)}
                            loading={removeTypeMutation.isPending}
                            disabled={!isRemoveTypeConfirmed}
                        >
                            Remove {typeToRemove ? getAccountTypeLabel(typeToRemove) : ''}
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <div className="p-4 bg-red/10 border border-red/20 rounded-lg">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="text-red flex-shrink-0 mt-0.5" size={20} />
                            <div>
                                <p className="font-medium text-red">
                                    You are about to remove the "{typeToRemove ? getAccountTypeLabel(typeToRemove) : ''}" status
                                </p>
                                <p className="text-sm text-muted-light dark:text-muted-dark mt-1">
                                    This action will remove the <strong>{typeToRemove ? getAccountTypeLabel(typeToRemove) : ''}</strong> account type from <strong>@{user?.username}</strong>. This may affect the user's permissions and visibility on the platform.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-2">
                            Type <strong>"{user?.username}"</strong> to confirm:
                        </label>
                        <Input
                            value={removeTypeConfirmation}
                            onChange={(e) => setRemoveTypeConfirmation(e.target.value)}
                            placeholder={user?.username}
                            className={removeTypeConfirmation && !isRemoveTypeConfirmed ? 'border-red' : ''}
                        />
                        {removeTypeConfirmation && !isRemoveTypeConfirmed && (
                            <p className="text-sm text-red mt-1">
                                Username doesn't match
                            </p>
                        )}
                    </div>
                </div>
            </Modal>

            {/* Change Username Modal */}
            <Modal
                isOpen={showChangeUsernameModal}
                onClose={closeChangeUsernameModal}
                title="Change Username"
                footer={
                    <>
                        <Button variant="secondary" onClick={closeChangeUsernameModal}>
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={() => changeUsernameMutation.mutate()}
                            loading={changeUsernameMutation.isPending}
                            disabled={!isUsernameChangeConfirmed}
                        >
                            <Edit3 size={18} />
                            Change Username
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <div className="p-4 bg-yellow/10 border border-yellow/20 rounded-lg">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="text-yellow flex-shrink-0 mt-0.5" size={20} />
                            <div>
                                <p className="font-medium text-yellow">
                                    This is a significant action
                                </p>
                                <p className="text-sm text-muted-light dark:text-muted-dark mt-1">
                                    Changing a username will update the user's profile URL. Links using the old username will no longer work. All mentions are stored by user ID so they will automatically reflect the new username.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div>
                        <p className="text-sm text-muted-light dark:text-muted-dark mb-2">
                            Current username: <strong>@{user?.username}</strong>
                        </p>
                    </div>

                    <Input
                        label="New Username"
                        value={newUsername}
                        onChange={(e) => setNewUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                        placeholder="new_username"
                    />
                    {newUsername && newUsername.length < 3 && (
                        <p className="text-sm text-red -mt-2">
                            Username must be at least 3 characters
                        </p>
                    )}
                    {newUsername && /^\d/.test(newUsername) && (
                        <p className="text-sm text-red -mt-2">
                            Username cannot start with a number
                        </p>
                    )}

                    <Textarea
                        label="Reason for change (optional)"
                        value={usernameReason}
                        onChange={(e) => setUsernameReason(e.target.value)}
                        placeholder="e.g., User requested name change, inappropriate username, etc."
                        rows={2}
                    />

                    <div>
                        <label className="block text-sm font-medium mb-2">
                            Type <strong>"{user?.username}"</strong> to confirm:
                        </label>
                        <Input
                            value={usernameConfirmation}
                            onChange={(e) => setUsernameConfirmation(e.target.value)}
                            placeholder={user?.username}
                            className={usernameConfirmation && usernameConfirmation !== user?.username ? 'border-red' : ''}
                        />
                        {usernameConfirmation && usernameConfirmation !== user?.username && (
                            <p className="text-sm text-red mt-1">
                                Username doesn't match
                            </p>
                        )}
                    </div>
                </div>
            </Modal>

            {/* Push Notification Modal */}
            <Modal
                isOpen={showPushModal}
                onClose={() => setShowPushModal(false)}
                title="Send Push Notification"
                footer={
                    <>
                        <Button
                            variant="secondary"
                            onClick={() => setShowPushModal(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => pushMutation.mutate()}
                            loading={pushMutation.isPending}
                            disabled={!pushNotification.title.trim() || !pushNotification.body.trim()}
                        >
                            <Bell size={18} />
                            Send Notification
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <Input
                        label="Title"
                        value={pushNotification.title}
                        onChange={(e) => setPushNotification({ ...pushNotification, title: e.target.value })}
                        placeholder="Notification title"
                        required
                    />
                    <Textarea
                        label="Message"
                        value={pushNotification.body}
                        onChange={(e) => setPushNotification({ ...pushNotification, body: e.target.value })}
                        placeholder="Notification message..."
                        rows={3}
                        required
                    />
                    <Input
                        label="Deep Link (optional)"
                        value={pushNotification.deepLink}
                        onChange={(e) => setPushNotification({ ...pushNotification, deepLink: e.target.value })}
                        placeholder="/(app)/settings or /(app)/post/123"
                    />
                    <p className="text-sm text-muted-light dark:text-muted-dark">
                        Deep links should start with <code className="bg-accent-light dark:bg-accent-dark px-1 rounded">/(app)/</code> for in-app navigation.
                    </p>
                </div>
            </Modal>

            {/* Hard Delete Modal - Super Admin Only */}
            <Modal
                isOpen={showHardDeleteModal}
                onClose={closeHardDeleteModal}
                title="⚠️ Permanently Delete User"
                footer={
                    <>
                        <Button
                            variant="secondary"
                            onClick={closeHardDeleteModal}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={() => hardDeleteMutation.mutate()}
                            loading={hardDeleteMutation.isPending}
                            disabled={!isHardDeleteConfirmed}
                            className="!bg-red-600 hover:!bg-red-700"
                        >
                            <Trash2 size={18} />
                            Permanently Delete User
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    {/* Critical Warning */}
                    <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="text-red-500 flex-shrink-0 mt-0.5" size={24} />
                            <div>
                                <p className="font-bold text-red-500 text-lg">
                                    CRITICAL: IRREVERSIBLE ACTION
                                </p>
                                <p className="text-sm text-red-400 mt-1">
                                    This will <strong>permanently delete</strong> the user and ALL their data. This action cannot be undone. Use this only for removing test/dev accounts.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* What will be deleted */}
                    <div className="p-4 bg-accent-light dark:bg-accent-dark rounded-lg">
                        <p className="font-medium mb-2">The following will be permanently removed:</p>
                        <ul className="text-sm text-muted-light dark:text-muted-dark space-y-1 list-disc list-inside">
                            <li>User account and profile</li>
                            <li>All {user?.stats?.posts || 0} posts and their comments</li>
                            <li>All media files (images, profile pictures)</li>
                            <li>All messages and conversations</li>
                            <li>All follows, votes, and notifications</li>
                            <li>Clerk authentication record</li>
                        </ul>
                    </div>

                    {/* User info */}
                    <div className="p-3 border border-border-light dark:border-border-dark rounded-lg">
                        <p className="text-sm text-muted-light dark:text-muted-dark mb-1">
                            User to delete:
                        </p>
                        <p className="font-medium">@{user?.username}</p>
                        <p className="text-sm text-muted-light dark:text-muted-dark">{user?.email}</p>
                    </div>

                    {/* Confirmation Steps */}
                    <div className="space-y-4">
                        {/* Step 1: Acknowledge */}
                        <label className="flex items-start gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={hardDeleteAcknowledged}
                                onChange={(e) => setHardDeleteAcknowledged(e.target.checked)}
                                className="mt-1 w-4 h-4 rounded border-border-light dark:border-border-dark"
                            />
                            <span className="text-sm">
                                I understand this action is <strong>permanent and irreversible</strong>. All user data will be completely removed from the system.
                            </span>
                        </label>

                        {/* Step 2: Type username */}
                        <div>
                            <label className="block text-sm font-medium mb-2">
                                Type <strong className="text-red-500">"{user?.username}"</strong> to confirm:
                            </label>
                            <Input
                                value={hardDeleteConfirmUsername}
                                onChange={(e) => setHardDeleteConfirmUsername(e.target.value)}
                                placeholder={user?.username}
                                className={hardDeleteConfirmUsername && hardDeleteConfirmUsername !== user?.username ? 'border-red' : ''}
                                disabled={!hardDeleteAcknowledged}
                            />
                        </div>

                        {/* Step 3: Type DELETE */}
                        <div>
                            <label className="block text-sm font-medium mb-2">
                                Type <strong className="text-red-500">"DELETE"</strong> to proceed:
                            </label>
                            <Input
                                value={hardDeleteConfirmDelete}
                                onChange={(e) => setHardDeleteConfirmDelete(e.target.value.toUpperCase())}
                                placeholder="DELETE"
                                className={hardDeleteConfirmDelete && hardDeleteConfirmDelete !== 'DELETE' ? 'border-red' : ''}
                                disabled={!hardDeleteAcknowledged || hardDeleteConfirmUsername !== user?.username}
                            />
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Add Environment Modal */}
            <Modal
                isOpen={showAddEnvModal}
                onClose={() => setShowAddEnvModal(false)}
                title="Add Environment Access"
                footer={
                    <>
                        <Button
                            variant="secondary"
                            onClick={() => setShowAddEnvModal(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => addEnvMutation.mutate()}
                            loading={addEnvMutation.isPending}
                            disabled={!selectedEnvironment}
                        >
                            <Globe size={18} />
                            Add Environment
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <p className="text-sm text-muted-light dark:text-muted-dark">
                        Adding a user to an environment allows them to see and interact with content in that environment. 
                        Users in <strong>test</strong> or <strong>dev</strong> environments will only see content from users in the same environment.
                    </p>
                    <Select
                        label="Environment"
                        value={selectedEnvironment}
                        onChange={(e) => setSelectedEnvironment(e.target.value)}
                        required
                    >
                        <option value="">Select environment...</option>
                        {['dev', 'test'].filter(env => !(user?.environments || ['prod']).includes(env)).map((env) => (
                            <option key={env} value={env}>
                                {env.toUpperCase()} - {env === 'test' ? 'Testing environment' : 'Development environment'}
                            </option>
                        ))}
                    </Select>
                    <div className="p-3 bg-blue/10 border border-blue/20 rounded-lg">
                        <p className="text-sm text-blue">
                            <strong>Note:</strong> All users always have access to the production environment. 
                            Adding test/dev access is additive.
                        </p>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
